import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  Container, AppBar, Toolbar, Typography, Box, Alert,
  CircularProgress, Paper, Chip, LinearProgress, Divider,
  Button, Tooltip, IconButton, Grid, Avatar, Pagination
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DownloadIcon from '@mui/icons-material/Download';
import { ScanInputContainer } from './components/ScanInput/ScanInputContainer';
import { ScanTarget } from '@shared/types';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { AdminPage } from './pages/AdminPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import './components/ScanInput/ScanInput.css';
import { useLang } from './context/LangContext';
import { LangSwitcher } from './components/LangSwitcher';

const PQC_KEX_PATTERNS_SSH = ['sntrup', 'mlkem', 'kyber', 'ntru', 'frodo'];
const BRAND_GRAD = 'linear-gradient(135deg, #bf360c 0%, #e65100 55%, #ff6d00 100%)';
const BRAND_PRIMARY = '#e65100';

function analyzePQCStatus(tlsResult: any) {
  const ciphers = tlsResult?.cipherSuites || [];
  const pqcAlgos = tlsResult?.pqcAlgorithms || [];
  const certs = tlsResult?.certificates || [];
  const kexAlg = ciphers[0]?.keyExchange || 'Unknown';
  const kexLower = kexAlg.toLowerCase();
  const kexIsPQC = pqcAlgos.some((a: any) => a.type === 'kem') ||
    ['kyber','mlkem','ntru','saber','frodo','bike'].some(k => kexLower.includes(k));
  const cert = certs[0];
  const sigAlg = cert?.signatureAlgorithm || '';
  const pkiIsPQC = ['dilithium','falcon','sphincs','mldsa','slhdsa'].some(k => sigAlg.toLowerCase().includes(k));
  const pkiAlg = sigAlg || cert?.publicKeyAlgorithm || 'Unknown';
  const risk = kexIsPQC && pkiIsPQC ? 'low' : kexIsPQC || pkiIsPQC ? 'medium' : 'high';
  return { kexStatus: kexIsPQC ? 'safe' : 'unsafe', kexAlgorithm: kexAlg, pkiStatus: pkiIsPQC ? 'safe' : 'unsafe', pkiAlgorithm: pkiAlg, risk } as const;
}

function RiskBadge({ risk }: { risk: 'high' | 'medium' | 'low' }) {
  const { T } = useLang();
  const cfg = {
    high:   { bg: 'linear-gradient(135deg,#c62828,#e53935)', label: T.highRisk },
    medium: { bg: 'linear-gradient(135deg,#e65100,#f57c00)', label: T.mediumRisk },
    low:    { bg: 'linear-gradient(135deg,#1b5e20,#2e7d32)', label: T.lowRisk },
  }[risk];
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, background: cfg.bg, color: '#fff', px: 2, py: 0.6, borderRadius: 2, fontWeight: 700, fontSize: 14 }}>
      {cfg.label}
    </Box>
  );
}

function StatusBadge({ status, label }: { status: 'safe' | 'unsafe' | 'unknown'; label: string }) {
  const safe = status === 'safe';
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: safe ? '#e8f5e9' : '#fce4ec', color: safe ? '#1b5e20' : '#b71c1c', px: 1.5, py: 0.4, borderRadius: 1.5, fontSize: 13, fontWeight: 600, border: `1px solid ${safe ? '#a5d6a7' : '#f48fb1'}` }}>
      {safe ? <CheckCircleIcon sx={{ fontSize: 15 }} /> : <CancelIcon sx={{ fontSize: 15 }} />}
      {label}
    </Box>
  );
}

async function exportToPDF() {
  const { default: jsPDF } = await import('jspdf');
  const { default: html2canvas } = await import('html2canvas');
  const el = document.getElementById('scan-results-export');
  if (!el) return;
  const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10; const imgW = pageW - margin * 2;
  const imgH = (canvas.height * imgW) / canvas.width;
  let srcY = 0; let remaining = imgH; let firstPage = true;
  while (remaining > 0) {
    const sliceH = Math.min(remaining, pageH - margin * 2);
    const srcSliceH = Math.round((sliceH / imgH) * canvas.height);
    const sc = document.createElement('canvas');
    sc.width = canvas.width; sc.height = srcSliceH;
    sc.getContext('2d')!.drawImage(canvas, 0, srcY, canvas.width, srcSliceH, 0, 0, canvas.width, srcSliceH);
    if (!firstPage) pdf.addPage();
    pdf.addImage(sc.toDataURL('image/png'), 'PNG', margin, margin, imgW, sliceH);
    remaining -= sliceH; srcY += srcSliceH; firstPage = false;
  }
  pdf.save(`pqc-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

function ScanResultCard({ result }: { result: any }) {
  const { T } = useLang();
  const tls = result.tlsResult;
  if (!tls) return null;
  const pqc = analyzePQCStatus(tls);
  const cert = tls.certificates?.[0];
  const riskAdvice: Record<string, string> = {
    high: T.adviceHigh,
    medium: pqc.kexStatus === 'safe' ? T.adviceMediumKex : T.adviceMediumPki,
    low: T.adviceLow,
  };
  const riskBorder = { high: '#ffcdd2', medium: '#ffe0b2', low: '#c8e6c9' }[pqc.risk];
  const riskBg = { high: '#fff5f5', medium: '#fff8f0', low: '#f0fff4' }[pqc.risk];
  return (
    <Paper sx={{ mb: 3, overflow: 'hidden', border: `1px solid ${riskBorder}` }}>
      <Box sx={{ px: 3, py: 2, bgcolor: riskBg, borderBottom: `1px solid ${riskBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <VerifiedUserIcon sx={{ color: { high: '#c62828', medium: '#e65100', low: '#2e7d32' }[pqc.risk] }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{result.target}</Typography>
            <Typography variant="caption" color="text.secondary">Port {tls.port} · {tls.tlsVersions.join(', ') || 'Unknown'}</Typography>
          </Box>
        </Box>
        <RiskBadge risk={pqc.risk} />
      </Box>
      <Box sx={{ p: 3 }}>
        {/* Risk badge above TLS version */}
        <Box sx={{ mb: 2 }}><RiskBadge risk={pqc.risk} /></Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{T.tlsVersion}</Typography>
            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {tls.tlsVersions.length > 0 ? tls.tlsVersions.map((v: string) => (
                <Chip key={v} label={v} size="small" color={v === 'TLS 1.3' ? 'success' : v === 'TLS 1.2' ? 'secondary' : 'error'} />
              )) : <Typography variant="body2" color="text.secondary">Unknown</Typography>}
            </Box>
          </Box>
          <Box sx={{ minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{T.kex}</Typography>
            <Box sx={{ mt: 0.5 }}><StatusBadge status={pqc.kexStatus} label={pqc.kexAlgorithm} /></Box>
          </Box>
          <Box sx={{ minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{T.pki}</Typography>
            <Box sx={{ mt: 0.5 }}><StatusBadge status={pqc.pkiStatus} label={pqc.pkiAlgorithm} /></Box>
          </Box>
          {tls.cipherSuites?.length > 0 && (
            <Box sx={{ minWidth: 160 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{T.cipherSuite}</Typography>
              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {tls.cipherSuites.map((c: any, i: number) => (
                  <Chip key={i} label={c.name} size="small" color={c.strength === 'strong' ? 'success' : c.strength === 'weak' ? 'error' : 'warning'} variant="outlined" sx={{ fontSize: 11 }} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
        {cert && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ bgcolor: '#fff8f0', borderRadius: 2, p: 2, border: '1px solid #ffe0b2' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <VerifiedUserIcon sx={{ fontSize: 18, color: BRAND_PRIMARY }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: BRAND_PRIMARY }}>{T.certificate}</Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '120px 1fr' }, gap: '6px 12px', alignItems: 'baseline' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{T.subjectDN}</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: 12 }}>{cert.subjectDN || cert.subject || 'Unknown'}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{T.issuerDN}</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: 12 }}>{cert.issuerDN || cert.issuer || 'Unknown'}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{T.validity}</Typography>
                <Typography variant="body2" sx={{ fontSize: 12 }}>{new Date(cert.validFrom).toLocaleDateString('zh-TW')} ～ {new Date(cert.validTo).toLocaleDateString('zh-TW')}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{T.publicKey}</Typography>
                <Typography variant="body2" sx={{ fontSize: 12 }}>{cert.publicKeyAlgorithm}{cert.keySize > 0 ? ` ${cert.keySize} bits` : ''}</Typography>
                {cert.supportedAlgorithms?.length > 0 && (<>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{T.algorithms}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {cert.supportedAlgorithms.map((alg: string, i: number) => <Chip key={i} label={alg} size="small" variant="outlined" sx={{ fontSize: 10 }} />)}
                  </Box>
                </>)}
              </Box>
              {cert.pem && (
                <Box sx={{ mt: 1.5 }}>
                  <Button size="small" variant="outlined" color="primary" startIcon={<DownloadIcon />}
                    href={`/api/scans/${result.scanId}/results/${encodeURIComponent(result.target)}/certificate`}
                    download={`${result.target}.pem`} sx={{ fontSize: 12 }}>
                    {T.downloadPEM}
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}

        <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: { high: '#fff3e0', medium: '#fff8e1', low: '#e8f5e9' }[pqc.risk], border: `1px solid ${riskBorder}`, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <LockIcon sx={{ fontSize: 18, mt: 0.2, color: { high: '#e65100', medium: '#f57c00', low: '#2e7d32' }[pqc.risk], flexShrink: 0 }} />
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{riskAdvice[pqc.risk]}</Typography>
        </Box>
      </Box>
    </Paper>
  );
}

function QuotaBadge({ refreshKey }: { refreshKey: number }) {
  const { T } = useLang();
  const { token } = useAuth();
  const [quota, setQuota] = useState<{ remaining: number | null; limit: number | null; unlimited: boolean } | null>(null);
  useEffect(() => {
    if (!token) return;
    fetch('/api/scans/quota', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.success) setQuota(d.data); });
  }, [token, refreshKey]);
  if (!quota || quota.unlimited) return null;
  const remaining = quota.remaining ?? 0;
  const limit = quota.limit ?? 10;
  const pct = remaining / limit;
  const color: any = pct <= 0.2 ? 'error' : pct <= 0.5 ? 'warning' : 'success';
  return (
    <Tooltip title={`${T.quotaTooltip}: ${remaining}/${limit}`}>
      <Chip label={`${remaining}/${limit}`} color={color} size="small" sx={{ mr: 1, fontWeight: 700, fontSize: 12 }} />
    </Tooltip>
  );
}


function ScanSummary({ results, scanDuration }: { results: any[]; scanDuration: number; T?: any }) {
  const { lang } = useLang();

  const online = results.filter(r => !r.tlsResult?.error).length;
  const offline = results.length - online;

  const isPqcKex = (r: any) => {
    const kex = r.tlsResult?.cipherSuites?.[0]?.keyExchange || '';
    return ['kyber','mlkem','ntru','saber','frodo','bike'].some(k => kex.toLowerCase().includes(k));
  };
  const isPqcSig = (r: any) => {
    const sig = r.tlsResult?.certificates?.[0]?.signatureAlgorithm || '';
    return ['dilithium','falcon','sphincs','mldsa','slhdsa'].some(k => sig.toLowerCase().includes(k));
  };
  const hasTls13 = (r: any) => (r.tlsResult?.tlsVersions || []).includes('TLS 1.3');

  const pqcKex = results.filter(r => !r.tlsResult?.error && isPqcKex(r)).length;
  const danger = results.filter(r => !r.tlsResult?.error && (!hasTls13(r) || (!isPqcKex(r) && !isPqcSig(r)))).length;
  const safe = results.filter(r => !r.tlsResult?.error && hasTls13(r) && isPqcKex(r) && isPqcSig(r)).length;

  const zh = lang === 'zh';
  const items = [
    {
      label: 'Total', value: results.length, color: '#1565c0',
      tooltip: zh
        ? '總掃描目標數量，包含所有提交的網站。'
        : 'Total number of scan targets submitted for analysis.',
    },
    {
      label: 'Online', value: online, color: '#2e7d32',
      tooltip: zh
        ? '成功建立 TLS 連線的網站數量。Online + Offline = Total。'
        : 'Targets that successfully completed TLS handshake. Online + Offline = Total.',
    },
    {
      label: 'Offline', value: offline, color: '#78909c',
      tooltip: zh
        ? '無法建立 TLS 連線的網站（DNS 解析失敗、連線逾時、拒絕連線等）。'
        : 'Targets that failed TLS connection (DNS failure, timeout, connection refused, etc.).',
    },
    {
      label: 'Danger', value: danger, color: '#c62828',
      tooltip: zh
        ? '不符合量子安全要求的網站：TLS 版本低於 1.3，或 TLS 1.3 但未使用 PQC 的 KEX 或 PKI 憑證。依據 NIST SP 800-208、CNSA 2.0。'
        : 'Sites not meeting quantum-safe requirements: TLS < 1.3, or TLS 1.3 without PQC KEX or PKI certificate. Per NIST SP 800-208, CNSA 2.0.',
    },
    {
      label: 'PQC KEX', value: pqcKex, color: '#e65100',
      tooltip: zh
        ? '使用量子安全 KEM 演算法（如 ML-KEM / Kyber）進行 TLS Hybrid Mode 金鑰交換的網站。依據 NIST FIPS 203 (ML-KEM)。'
        : 'Sites using quantum-safe KEM algorithms (e.g., ML-KEM / Kyber) for TLS Hybrid Mode Key Exchange. Per NIST FIPS 203 (ML-KEM).',
    },
    {
      label: 'SAFE', value: safe, color: '#1b5e20',
      tooltip: zh
        ? '完全符合 NIST 量子安全要求：TLS 1.3 + PQC KEX (ML-KEM) + PQC PKI 簽章 (ML-DSA)。依據 NIST SP 800-208、FIPS 203、FIPS 204。'
        : 'Fully NIST quantum-safe compliant: TLS 1.3 + PQC KEX (ML-KEM) + PQC PKI signature (ML-DSA). Per NIST SP 800-208, FIPS 203, FIPS 204.',
    },
    {
      label: zh ? '耗時' : 'Duration', value: (scanDuration / 1000).toFixed(1) + 's', color: '#6a1b9a',
      tooltip: zh
        ? '所有目標掃描的累計時間（秒）。'
        : 'Cumulative scan duration across all targets (seconds).',
    },
  ];

  return (
    <Paper sx={{ p: 2, mb: 3, border: '1px solid #ffe0b2' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
        {items.map(item => (
          <Tooltip key={item.label} title={<Typography variant="body2" sx={{ p: 0.5, lineHeight: 1.6 }}>{item.tooltip}</Typography>} arrow placement="top">
            <Box sx={{ textAlign: 'center', minWidth: 70, cursor: 'help' }}>
              <Typography variant="h5" fontWeight={800} sx={{ color: item.color }}>{item.value}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{item.label}</Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Paper>
  );
}

function HomePage() {
  const { T } = useLang();
  const { user, token, logout } = useAuth();
  const [isScanning, setIsScanning] = useState(false);
  const [scanId, setScanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<any>(null);
  const [results, setResults] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);
  const [quotaRefresh, setQuotaRefresh] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Set<string>>(new Set(['all']));
  const [scanDuration, setScanDuration] = useState(0);
  const features = user?.enabledFeatures || [];
  const isBatch = features.includes('file_upload_scan');
  const isIpRange = features.includes('ip_range_scan');
  const isEnterprise = features.includes('ssh_scan');

  useEffect(() => {
    if (!scanId) return;
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`/api/scans/${scanId}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (data.success) {
          setScanStatus(data.data);
          if (data.data.status === 'completed' || data.data.status === 'failed') {
            clearInterval(poll);
            const rRes = await fetch(`/api/scans/${scanId}/results?limit=50000`, { headers: { Authorization: `Bearer ${token}` } });
            const rData = await rRes.json();
            if (rData.success) {
              setResults(rData.data || []);
              const dur = (rData.data || []).reduce((s: number, r: any) => s + (r.tlsResult?.scanDuration || 0), 0);
              setScanDuration(dur);
            }
          }
        }
      } catch {}
    }, 1000);
    return () => clearInterval(poll);
  }, [scanId]);

  const handleStartScan = async (targets: ScanTarget[]) => {
    setIsScanning(true); setError(null); setScanId(null); setScanStatus(null); setResults([]); setPage(1); setScanDuration(0);
    try {
      const res = await fetch('/api/scans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ targets }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Scan failed to start');
      setScanId(data.data.scanId);
      setQuotaRefresh(k => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally { setIsScanning(false); }
  };

  const handleProtocolResult = (data: any) => {
    // Convert protocol scan results to main results format
    const converted: any[] = [];
    const target = data.target;

    // TLS result
    if (data.results.tls) {
      converted.push({
        scanId: 'protocol',
        target: target,
        tlsResult: data.results.tls,
      });
    }

    // SSH result as a separate "target"
    if (data.results.ssh && data.results.ssh.available) {
      const ssh = data.results.ssh;
      const pqcKexAlg = ssh.kexAlgorithms?.find((k: string) => ['sntrup','mlkem','kyber'].some(p => k.toLowerCase().includes(p))) || '';
      converted.push({
        scanId: 'protocol',
        target: `${target} (SSH:22)`,
        tlsResult: {
          target: `${target} (SSH:22)`,
          port: 22,
          tlsVersions: ['SSH-2.0'],
          cipherSuites: [{
            name: ssh.ciphers?.[0] || 'SSH',
            protocol: 'SSH-2.0',
            keyExchange: pqcKexAlg || ssh.kexAlgorithms?.[0] || 'Unknown',
            authentication: ssh.hostKeyAlgorithms?.[0] || 'Unknown',
            encryption: ssh.ciphers?.[0] || 'Unknown',
            mac: ssh.macs?.[0] || 'Unknown',
            strength: ssh.pqcKex ? 'strong' : 'medium',
            deprecated: false,
          }],
          pqcAlgorithms: ssh.pqcKex ? [{ name: pqcKexAlg, type: 'kem', category: 'lattice', nistLevel: 3, keySize: 1184 }] : [],
          certificates: [],
          scanTimestamp: new Date(),
          scanDuration: ssh.scanDuration || 0,
        },
      });
    }

    // Email results
    if (data.results.email) {
      for (const e of data.results.email) {
        if (!e.available) continue;
        converted.push({
          scanId: 'protocol',
          target: `${target} (${e.protocol}:${e.port})`,
          tlsResult: {
            target: `${target} (${e.protocol}:${e.port})`,
            port: e.port,
            tlsVersions: e.tlsVersion ? [e.tlsVersion] : [],
            cipherSuites: e.cipher ? [{ name: e.cipher, protocol: e.tlsVersion || '', keyExchange: e.kexGroup || 'Unknown', authentication: 'Unknown', encryption: 'Unknown', mac: 'Unknown', strength: e.pqcKex ? 'strong' : 'medium', deprecated: false }] : [],
            pqcAlgorithms: [],
            certificates: e.certSubject ? [{ subject: e.certSubject, issuer: '', subjectDN: e.certSubject, issuerDN: '', validFrom: new Date(), validTo: new Date(), serialNumber: '', signatureAlgorithm: e.sigAlg || '', publicKeyAlgorithm: '', keySize: 0, fingerprint: '' }] : [],
            scanTimestamp: new Date(),
            scanDuration: 0,
          },
        });
      }
    }

    setResults(converted);
    setScanDuration(data.scanDuration || 0);
    setPage(1);
    setFilters(new Set(['all']));
  };

  const roleLabel: Record<string, string> = { admin: 'Admin', enterprise: 'Enterprise', advanced: 'Advanced', standard: 'Standard', basic: 'Basic' };
  const roleColor: any = { admin: 'error', enterprise: 'warning', advanced: 'secondary', standard: 'primary', basic: 'default' };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff3e0' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src="/logo.png" alt="DB.ai" style={{ width: 36, height: 36, borderRadius: 6, objectFit: "contain", background: "#fff", padding: 2 }} />
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', lineHeight: 1, fontSize: 10, letterSpacing: 0.3 }}>DataBrushing AI</Typography>
              <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.2 }}>PQC Navigator</Typography>
            </Box>
            <Typography variant="h6" fontWeight={800} sx={{ display: { xs: 'block', md: 'none' } }}>PQC Navigator</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <QuotaBadge refreshKey={quotaRefresh} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 13 }}>
              {(user?.displayName || user?.email || '?')[0].toUpperCase()}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ lineHeight: 1.2, opacity: 0.9 }}>{user?.displayName || user?.email}</Typography>
              <Chip label={roleLabel[user?.role || 'basic']} color={roleColor[user?.role || 'basic']} size="small" sx={{ height: 16, fontSize: 10 }} />
            </Box>
          </Box>
          {user?.role === 'admin' && (
            <Tooltip title={T.adminPanel}>
              <IconButton color="inherit" href="/admin" size="small"><AdminPanelSettingsIcon /></IconButton>
            </Tooltip>
          )}
          <Tooltip title={T.logout}>
            <IconButton color="inherit" onClick={logout} size="small"><LogoutIcon /></IconButton>
          </Tooltip>
          <Box sx={{ ml: 1 }}><LangSwitcher light /></Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ background: BRAND_GRAD, color: '#fff', py: { xs: 4, md: 5 }, px: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={800} sx={{ mb: 1, textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}>{T.heroTitle}</Typography>
          <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 700, textShadow: '0 1px 6px rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>{T.heroSubtitle}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.75, mt: 1, display: 'block', textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>{T.heroCaption}</Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <ScanInputContainer onStartScan={handleStartScan} isScanning={isScanning} isAdvanced={isBatch} isIpRange={isIpRange} isEnterprise={isEnterprise} token={token} enabledFeatures={features} onProtocolResult={handleProtocolResult} />
        </Paper>

        {isScanning && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}><CircularProgress size={18} color="primary" /><Typography color="text.secondary">{T.scanStarting}</Typography></Box>}
        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

        {scanId && scanStatus && scanStatus.status !== 'completed' && (
          <Paper sx={{ p: 3, mb: 3, border: '1px solid #ffe0b2' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={20} thickness={5} color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>{T.scanInProgress}</Typography>
              <Chip label={scanStatus.status} color="primary" size="small" />
            </Box>
            <LinearProgress variant="determinate" value={scanStatus.progress || 0} sx={{ mb: 1, height: 8, borderRadius: 4 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                {scanStatus.completedTargets}/{scanStatus.totalTargets} {T.targetsCompleted}{scanStatus.currentTarget ? ` · ${T.scanning} ${scanStatus.currentTarget}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">{scanStatus.progress || 0}%</Typography>
            </Box>
          </Paper>
        )}

        {results.length > 0 && (() => {
          // Filter logic
          const isPqcKex = (r: any) => {
            const kex = r.tlsResult?.cipherSuites?.[0]?.keyExchange || '';
            return ['kyber','mlkem','ntru','saber','frodo','bike'].some(k => kex.toLowerCase().includes(k));
          };
          const isPqcSig = (r: any) => {
            const sig = r.tlsResult?.certificates?.[0]?.signatureAlgorithm || '';
            return ['dilithium','falcon','sphincs','mldsa','slhdsa'].some(k => sig.toLowerCase().includes(k));
          };
          const hasTls13 = (r: any) => (r.tlsResult?.tlsVersions || []).includes('TLS 1.3');
          const isOnline = (r: any) => !r.tlsResult?.error;
          const isDanger = (r: any) => isOnline(r) && (!hasTls13(r) || (!isPqcKex(r) && !isPqcSig(r)));
          const isSafe = (r: any) => isOnline(r) && hasTls13(r) && isPqcKex(r) && isPqcSig(r);

          const filteredResults = filters.has('all') ? results : results.filter(r => {
            if (filters.has('danger') && isDanger(r)) return true;
            if (filters.has('pqc_kex') && isPqcKex(r) && isOnline(r)) return true;
            if (filters.has('safe') && isSafe(r)) return true;
            if (filters.has('offline') && !isOnline(r)) return true;
            return false;
          });

          const toggleFilter = (f: string) => {
            if (f === 'all') { setFilters(new Set(['all'])); setPage(1); return; }
            const next = new Set(filters);
            next.delete('all');
            if (next.has(f)) next.delete(f); else next.add(f);
            if (next.size === 0) next.add('all');
            setFilters(next);
            setPage(1);
          };

          const dangerCount = results.filter(r => isDanger(r)).length;
          const pqcKexCount = results.filter(r => isPqcKex(r) && isOnline(r)).length;
          const safeCount = results.filter(r => isSafe(r)).length;
          const offlineCount = results.filter(r => !isOnline(r)).length;

          const filterBtns = [
            { key: 'all', label: `ALL ${results.length}`, color: '#1565c0' },
            { key: 'danger', label: `Danger ${dangerCount}`, color: '#c62828' },
            { key: 'pqc_kex', label: `PQC KEX ${pqcKexCount}`, color: '#e65100' },
            { key: 'safe', label: `SAFE ${safeCount}`, color: '#1b5e20' },
            { key: 'offline', label: `Offline ${offlineCount}`, color: '#78909c' },
          ];

          return (
          <Box>
            {/* Summary */}
            <ScanSummary results={results} scanDuration={scanDuration} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={700}>
                  {T.scanResults} <Chip label={`${filteredResults.length} ${T.targets}`} size="small" sx={{ ml: 1 }} />
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
                  {filterBtns.map(fb => (
                    <Chip key={fb.key} label={fb.label} size="small" clickable
                      onClick={() => toggleFilter(fb.key)}
                      sx={{
                        fontWeight: 700, fontSize: 11,
                        bgcolor: filters.has(fb.key) ? fb.color : 'transparent',
                        color: filters.has(fb.key) ? '#fff' : fb.color,
                        border: `1.5px solid ${fb.color}`,
                        '&:hover': { bgcolor: fb.color, color: '#fff', opacity: 0.85 },
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Button variant="contained" color="primary"
                startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                onClick={async () => {
                  setExporting(true);
                  try {
                    // Server-side PDF for large datasets
                    const link = document.createElement('a');
                    link.href = `/api/scans/${scanId}/export-pdf`;
                    link.download = `pqc-report-${scanId}.pdf`;
                    // Add auth header via fetch
                    const resp = await fetch(`/api/scans/${scanId}/export-pdf`, { headers: { Authorization: `Bearer ${token}` } });
                    const blob = await resp.blob();
                    link.href = URL.createObjectURL(blob);
                    link.click();
                    URL.revokeObjectURL(link.href);
                  } finally { setExporting(false); }
                }}
                disabled={exporting || !features.includes('pdf_export')}
                title={!features.includes('pdf_export') ? (T.exportPDF + ' 🔒') : undefined}>
                {exporting ? T.generating : T.exportPDF}
              </Button>
            </Box>

            {/* Paginated results */}
            <Box id="scan-results-export">
              {filteredResults.slice((page - 1) * 25, page * 25).map((r: any, i: number) => <ScanResultCard key={i} result={r} />)}
            </Box>
            {filteredResults.length > 25 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={Math.ceil(filteredResults.length / 25)} page={page} onChange={(_, v) => { setPage(v); window.scrollTo(0, 400); }} color="primary" />
              </Box>
            )}
          </Box>
          );
        })()}

        <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid #ffe0b2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">{T.footerLeft}</Typography>
          <Typography variant="caption" color="text.secondary">{T.footerRight}</Typography>
        </Box>
      </Container>
    </Box>
  );
}

function ProtectedHome() {
  const { user, loading } = useAuth();
  const [verified, setVerified] = useState(false);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('verified') === '1') { setVerified(true); window.history.replaceState({}, '', '/'); }
  }, []);
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!user) return <LoginPage verifiedAlert={verified} />;
  return <HomePage />;
}

function ProtectedAdmin() {
  const { user, loading } = useAuth();
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!user) return <Navigate to="/" />;
  if (user.role !== 'admin') return <Navigate to="/" />;
  return <AdminPage />;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<ProtectedHome />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/admin" element={<ProtectedAdmin />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
